import {createFeatureSelector, createSelector} from '@ngrx/store';
import {buildPaginationOptions} from '../../../utils/pagination-utils';
import {CATALOG_DETAIL_STATE_KEY, CatalogDetailState} from './catalog-detail.reducer';

export const selectCatalogDetailState = createFeatureSelector<CatalogDetailState>(CATALOG_DETAIL_STATE_KEY);

export const selectCatalogDetail = createSelector(selectCatalogDetailState, state => state.catalog);

export const selectCatalogDetailLoading = createSelector(selectCatalogDetailState, state => state.loading);

export const selectCatalogDetailError = createSelector(selectCatalogDetailState, state => state.error);

export const selectCatalogRegistrations = createSelector(selectCatalogDetailState, state => state.registrations);

export const selectCatalogRegistrationsLoading = createSelector(
  selectCatalogDetailState,
  state => state.registrationsLoading
);

export const selectCatalogRegistrationsError = createSelector(
  selectCatalogDetailState,
  state => state.registrationsError
);

export const selectCatalogDetailUpdateStatus = createSelector(selectCatalogDetailState, state => state.updateStatus);

export const selectCatalogDetailUpdating = createSelector(
  selectCatalogDetailUpdateStatus,
  status => status === 'loading'
);

export const selectCatalogDetailUpdateError = createSelector(selectCatalogDetailState, state => state.updateError);

export const selectCatalogProducts = createSelector(selectCatalogDetailState, state => state.products.products);

export const selectCatalogProductsLoading = createSelector(selectCatalogDetailState, state => state.products.loading);

export const selectCatalogProductsError = createSelector(selectCatalogDetailState, state => state.products.error);

export const selectCatalogProductsQuery = createSelector(selectCatalogDetailState, state => state.products.query);

export const selectCatalogProductQuery = createSelector(selectCatalogProductsQuery, query => query.query ?? '');

export const selectCatalogProductSortBy = createSelector(selectCatalogProductsQuery, query => query.sortBy);

export const selectCatalogProductSortDirection = createSelector(selectCatalogProductsQuery, query => query.direction);

export const selectCatalogProductsPaginationOptions = createSelector(selectCatalogDetailState, state =>
  buildPaginationOptions(state.products.paginationMeta, {label: 'Products'})
);

export const selectCatalogSelectedProductIds = createSelector(
  selectCatalogDetailState,
  state => new Set(state.products.selectedProductIds)
);

export const selectCatalogBundles = createSelector(selectCatalogDetailState, state => state.bundles.bundles);

export const selectCatalogBundlesLoading = createSelector(selectCatalogDetailState, state => state.bundles.loading);

export const selectCatalogBundlesLoaded = createSelector(selectCatalogDetailState, state => state.bundles.loaded);

export const selectCatalogBundlesError = createSelector(selectCatalogDetailState, state => state.bundles.error);

export const selectCatalogBundlesQuery = createSelector(selectCatalogDetailState, state => state.bundles.query);

export const selectCatalogBundleQuery = createSelector(selectCatalogBundlesQuery, query => query.query ?? '');

export const selectCatalogBundleSortBy = createSelector(selectCatalogBundlesQuery, query => query.sortBy);

export const selectCatalogBundleSortDirection = createSelector(selectCatalogBundlesQuery, query => query.direction);

export const selectCatalogBundlesPaginationOptions = createSelector(selectCatalogDetailState, state =>
  buildPaginationOptions(state.bundles.paginationMeta, {label: 'Bundles'})
);

export const selectCatalogSelectedBundleIds = createSelector(
  selectCatalogDetailState,
  state => new Set(state.bundles.selectedBundleIds)
);

export const selectCatalogProductSelection = createSelector(selectCatalogDetailState, state => state.productSelection);

export const selectCatalogProductSelectionProducts = createSelector(
  selectCatalogDetailState,
  state => state.productSelection.products
);

export const selectCatalogProductSelectionLoading = createSelector(
  selectCatalogDetailState,
  state => state.productSelection.loading
);

export const selectCatalogProductSelectionError = createSelector(
  selectCatalogDetailState,
  state => state.productSelection.error
);

export const selectCatalogProductSelectionQuery = createSelector(
  selectCatalogDetailState,
  state => state.productSelection.query
);

export const selectCatalogProductSelectionSearchQuery = createSelector(
  selectCatalogDetailState,
  state => state.productSelection.query.query ?? ''
);

export const selectCatalogProductSelectionSortBy = createSelector(
  selectCatalogDetailState,
  state => state.productSelection.query.sortBy
);

export const selectCatalogProductSelectionSortDirection = createSelector(
  selectCatalogDetailState,
  state => state.productSelection.query.direction
);

export const selectCatalogProductSelectionPaginationOptions = createSelector(selectCatalogDetailState, state =>
  buildPaginationOptions(state.productSelection.paginationMeta, {label: 'Products', defaultLimit: 25})
);

export const selectCatalogProductSelectionSelectedIds = createSelector(
  selectCatalogDetailState,
  state => new Set(state.productSelection.selectedProductIds)
);

export const selectCatalogProductSelectionSelectedCount = createSelector(
  selectCatalogDetailState,
  state => state.productSelection.selectedProductIds.length
);

export const selectCatalogProductSelectionSaving = createSelector(
  selectCatalogDetailState,
  state => state.productSelection.saveStatus === 'loading'
);

export const selectCatalogProductSelectionSaveStatus = createSelector(
  selectCatalogDetailState,
  state => state.productSelection.saveStatus
);

export const selectCatalogProductSelectionSaveError = createSelector(
  selectCatalogDetailState,
  state => state.productSelection.saveError
);

export const selectCatalogBundleSelection = createSelector(selectCatalogDetailState, state => state.bundleSelection);

export const selectCatalogBundleSelectionBundles = createSelector(
  selectCatalogDetailState,
  state => state.bundleSelection.bundles
);

export const selectCatalogBundleSelectionLoading = createSelector(
  selectCatalogDetailState,
  state => state.bundleSelection.loading
);

export const selectCatalogBundleSelectionError = createSelector(
  selectCatalogDetailState,
  state => state.bundleSelection.error
);

export const selectCatalogBundleSelectionQuery = createSelector(
  selectCatalogDetailState,
  state => state.bundleSelection.query
);

export const selectCatalogBundleSelectionSearchQuery = createSelector(
  selectCatalogBundleSelectionQuery,
  query => query.query ?? ''
);

export const selectCatalogBundleSelectionSortBy = createSelector(
  selectCatalogBundleSelectionQuery,
  query => query.sortBy
);

export const selectCatalogBundleSelectionSortDirection = createSelector(
  selectCatalogBundleSelectionQuery,
  query => query.direction
);

export const selectCatalogBundleSelectionPaginationOptions = createSelector(selectCatalogDetailState, state =>
  buildPaginationOptions(state.bundleSelection.paginationMeta, {label: 'Bundles', defaultLimit: 25})
);

export const selectCatalogBundleSelectionSelectedIds = createSelector(
  selectCatalogDetailState,
  state => new Set(state.bundleSelection.selectedBundleIds)
);

export const selectCatalogBundleSelectionSelectedCount = createSelector(
  selectCatalogDetailState,
  state => state.bundleSelection.selectedBundleIds.length
);

export const selectCatalogBundleSelectionSaving = createSelector(
  selectCatalogDetailState,
  state => state.bundleSelection.saveStatus === 'loading'
);

export const selectCatalogBundleSelectionSaveStatus = createSelector(
  selectCatalogDetailState,
  state => state.bundleSelection.saveStatus
);

export const selectCatalogBundleSelectionSaveError = createSelector(
  selectCatalogDetailState,
  state => state.bundleSelection.saveError
);
